import { ChangeEvent, useCallback, useState, useRef, useEffect } from "react";
import { useSetAtom } from "jotai";
import {
  BetaMenuTriggerRoot,
  BetaMenuTriggerPopover,
  BetaMenuTriggerList,
  BetaMenuTriggerItem,
  Button,
  Text,
  TextInput,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  closeSecondaryToolbarMenuSetter,
  takeSnapshotSetter,
  DisplayLinkAsType,
} from "@zapier/canvas-state";
import { isValidUrl, normalizeUrl } from "@zapier/rich-text-editor";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import styles from "./FloatingLinkEmbed.module.css";
import { EmbedExamples } from "./EmbedExamples";

const COPY = {
  title: "Embed link",
  description:
    "Paste a link to a third-party app, dashboard, document, or resource. Only publicly accessible or embeddable links will have a preview.",
  displayAs: "Display as:\u00a0",
  validationError:
    "This doesn\u2019t look like a valid URL. Check the link and try again.",
  embedOption: "Embed",
  urlOption: "URL",
} as const;

const DISPLAY_TYPE_LABELS: Record<DisplayLinkAsType, string> = {
  url: COPY.urlOption,
  embed: COPY.embedOption,
};

type Props = {
  link: string | undefined;
  displayLinkAs?: DisplayLinkAsType;
};

export function FloatingLinkEmbed({ link, displayLinkAs }: Props) {
  const [currentLink, setCurrentLink] = useState<string | undefined>(link);
  const [isError, setIsError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  const defaultDisplayLinkAs = displayLinkAs ?? "embed";
  const [currentDisplayLinkAs, setCurrentDisplayLinkAs] =
    useState<DisplayLinkAsType>(defaultDisplayLinkAs);

  // Auto-focus and optionally auto-select text
  useEffect(() => {
    if (inputRef.current) {
      // Small delay to ensure DOM is ready before focusing and selecting
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
        // Auto-select all text when editing existing link
        if (link) {
          inputRef.current?.select();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [link]);

  const validateAndSave = useCallback(() => {
    const trimmed = currentLink!.trim();
    const normalizedLink = normalizeUrl(trimmed);
    const isValid = isValidUrl(normalizedLink, { allowMedia: true });
    setIsError(!isValid);

    if (
      isValid &&
      (normalizedLink !== link || currentDisplayLinkAs !== displayLinkAs)
    ) {
      takeSnapshot();
      updateNodeAndSave({
        data: { link: normalizedLink, displayLinkAs: currentDisplayLinkAs },
      });

      closeSecondaryMenu();
    }
  }, [
    displayLinkAs,
    currentDisplayLinkAs,
    closeSecondaryMenu,
    currentLink,
    link,
    takeSnapshot,
    updateNodeAndSave,
  ]);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setCurrentLink(newValue);

      // Clear error state when user starts typing (gives immediate feedback)
      if (isError) {
        setIsError(false);
      }
    },
    [isError],
  );

  const handleRemoveLink = useCallback(() => {
    setCurrentLink("");
    setIsError(false);
    takeSnapshot();
    updateNodeAndSave({
      data: { link: undefined, displayLinkAs: undefined },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  const handleDisplayTypeSelect = useCallback((type: DisplayLinkAsType) => {
    setCurrentDisplayLinkAs(type);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === "Enter" &&
        currentLink?.trim() &&
        (currentLink !== link || currentDisplayLinkAs !== displayLinkAs)
      ) {
        e.preventDefault();
        validateAndSave();
      }
    },
    [currentLink, link, validateAndSave, currentDisplayLinkAs, displayLinkAs],
  );

  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <div className={styles.floatingTypeaheadWrapper}>
          <div className={styles.header}>
            <Text type="Body3" color="TextInverted">
              {COPY.title}
            </Text>
            <button
              className={styles.closeButton}
              onClick={closeSecondaryMenu}
              aria-label="Close"
            >
              <Icon name="formX" color="TextInverted" />
            </button>
          </div>
          <Text type="MinimalPrint1" color="TextInverted">
            {COPY.description}
          </Text>
          {/* nopan className prevents zooming when you triple click to select all text in the input */}
          <TextInput
            ref={inputRef}
            className="nopan"
            name="url"
            placeholder="Paste link here"
            inputMode="url"
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            value={currentLink}
            isErrored={isError}
            renderIconAfter={(iconProps) =>
              currentLink ? (
                <Tooltip placement="left" content="Remove link">
                  <TooltipFocusableTrigger>
                    <button
                      type="button"
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLink();
                      }}
                      aria-label="Clear input"
                    >
                      <Icon
                        {...iconProps}
                        name="formXCircle"
                        size={iconProps.iconSize}
                      />
                    </button>
                  </TooltipFocusableTrigger>
                </Tooltip>
              ) : null
            }
          />
          {isError && (
            <div className={styles.errorContainer}>
              <Text color="StatusError" type="MinimalPrint1Multiline">
                {COPY.validationError}
              </Text>
            </div>
          )}
          <div className={styles.buttonRow}>
            <div className={`nopan ${styles.buttonWrapper}`}>
              <BetaMenuTriggerRoot>
                <Button size="compact" variant="tertiary" iconPosition="end">
                  <div className={styles.displayAsText}>
                    <div>
                      <Text type="MinimalPrint3" color="TextDefault">
                        {COPY.displayAs}
                      </Text>
                      <Text type="MinimalPrint1" color="TextWeakest">
                        {DISPLAY_TYPE_LABELS[currentDisplayLinkAs]}
                      </Text>
                    </div>
                    <span className={styles.menuArrow}>
                      <Icon aria-hidden name="arrowSmallDown" />
                    </span>
                  </div>
                </Button>
                <BetaMenuTriggerPopover>
                  <BetaMenuTriggerList
                    onAction={(key) =>
                      handleDisplayTypeSelect(key as DisplayLinkAsType)
                    }
                  >
                    <BetaMenuTriggerItem id="embed">
                      <div className={styles.row}>
                        <Icon
                          name="arrowCode"
                          color="StrokeStronger"
                          size={17}
                        />
                        <span className={styles.title}>{COPY.embedOption}</span>
                      </div>
                    </BetaMenuTriggerItem>
                    <BetaMenuTriggerItem id="url">
                      <div className={styles.row}>
                        <Icon
                          name="actionLink"
                          color="StrokeStronger"
                          size={17}
                        />
                        <span className={styles.title}>{COPY.urlOption}</span>
                      </div>
                    </BetaMenuTriggerItem>
                  </BetaMenuTriggerList>
                </BetaMenuTriggerPopover>
              </BetaMenuTriggerRoot>
            </div>
            <Button
              size="compact"
              variant="primary"
              onPress={validateAndSave}
              isDisabled={
                !currentLink?.trim() ||
                (Boolean(link && currentLink === link) &&
                  Boolean(
                    displayLinkAs && currentDisplayLinkAs === displayLinkAs,
                  ))
              }
            >
              {link ? "Update" : "Add"}
            </Button>
          </div>
          <EmbedExamples />
        </div>
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
